import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  defaultPublisherListFilter,
  isPublisherSortDirection,
  isPublisherSortProperty,
  PublisherStatus,
  type PublisherListFilter,
  type PublisherSortDirection,
  type PublisherSortProperty,
} from './model';

export type PublishersRouteSearch = {
  search?: string;
  statuses?: string;
  page?: number;
  size?: number;
  sortProperty?: PublisherSortProperty | null;
  sortDirection?: PublisherSortDirection;
};

export function publishersRouteSearchSchema(
  search: Record<string, unknown>,
): PublishersRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const statuses = typeof search['statuses'] === 'string' ? search['statuses'] : undefined;
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  const sortProperty =
    typeof search['sortProperty'] === 'string' && isPublisherSortProperty(search['sortProperty'])
      ? search['sortProperty']
      : undefined;
  const sortDirection =
    typeof search['sortDirection'] === 'string' && isPublisherSortDirection(search['sortDirection'])
      ? search['sortDirection']
      : undefined;
  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(statuses !== undefined ? { statuses } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

const publisherStatuses: ReadonlySet<string> = new Set(Object.values(PublisherStatus));

export function parsePublishersRouteSearch(search: PublishersRouteSearch): PublisherListFilter {
  const statuses =
    search.statuses === undefined
      ? defaultPublisherListFilter.statuses
      : search.statuses
          .split(',')
          .filter((status): status is PublisherStatus => publisherStatuses.has(status));
  return {
    searchTerm: search.search ?? defaultPublisherListFilter.searchTerm,
    statuses,
    page: search.page ? search.page - 1 : defaultPublisherListFilter.page,
    size: search.size ?? defaultPublisherListFilter.size,
    sortProperty:
      search.sortProperty === null
        ? null
        : (search.sortProperty ?? defaultPublisherListFilter.sortProperty),
    sortDirection: search.sortDirection ?? defaultPublisherListFilter.sortDirection,
  };
}

export function serializePublishersRouteSearch(filter: PublisherListFilter): PublishersRouteSearch {
  const statuses = filter.statuses.join(',');
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(!equivalentStatusSets(filter.statuses, defaultPublisherListFilter.statuses)
      ? { statuses }
      : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultPublisherListFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultPublisherListFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultPublisherListFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}

function equivalentStatusSets(left: PublisherStatus[], right: PublisherStatus[]) {
  const leftSet = new Set(left);
  const rightSet = new Set(right);
  return leftSet.size === rightSet.size && [...leftSet].every((status) => rightSet.has(status));
}
